import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TeamChampionshipWeeksCard from '../../ChampionshipWeeks/TeamChampionshipWeeksCard';
import ChampionshipMatchupCompareModal from '../../ChampionshipWeeks/ChampionshipMatchupCompareModal';
import { prefetchTeamLogos, TEAM_LOGO_CACHE_SIZE } from '../../../utils/teamLogoCache';

const CHAMPIONSHIP_WEEKS_INDEX_URL = '/api/v1/best_ball/championship_weeks';
const teamPlayersUrl = (teamId) =>
	`/api/v1/best_ball/championship_weeks/${teamId}/players`;

const PLAYOFF_WEEKS = [
	15,
	16,
	17
];

const ChampionshipWeeksModal = ({ isOpen, onClose, player }) => {
	const playerTeamId = player && (player.team_id || (player.team && player.team.id));
	const playerName = (player && player.first_name) || 'Player';

	const [
		teams,
		setTeams
	] = useState([]);
	const [
		isLoading,
		setIsLoading
	] = useState(false);
	const [
		loadError,
		setLoadError
	] = useState('');
	const [
		expandedTeamId,
		setExpandedTeamId
	] = useState(null);
	const [
		playerSlots,
		setPlayerSlots
	] = useState({});
	const [
		selectedMatchup,
		setSelectedMatchup
	] = useState(null);

	useEffect(
		() => {
			if (!isOpen) return undefined;

			const handleEscape = (event) => {
				if (event.key === 'Escape') onClose();
			};

			document.body.style.overflow = 'hidden';
			document.addEventListener('keydown', handleEscape);

			return () => {
				document.body.style.overflow = '';
				document.removeEventListener('keydown', handleEscape);
			};
		},
		[
			isOpen,
			onClose
		]
	);

	useEffect(
		() => {
			if (!isOpen) return undefined;

			let cancelled = false;
			setIsLoading(true);
			setLoadError('');
			setExpandedTeamId(null);
			setPlayerSlots({});
			setSelectedMatchup(null);

			fetch(CHAMPIONSHIP_WEEKS_INDEX_URL, {
				credentials: 'same-origin',
				headers: { Accept: 'application/json' }
			})
				.then(async (response) => {
					const payload = await response.json();
					if (!response.ok || !payload.success) {
						throw new Error(
							payload.error || 'Could not load championship weeks.'
						);
					}
					if (!cancelled) {
						const nextTeams = (payload.data && payload.data.teams) || [];
						setTeams(nextTeams);
						prefetchTeamLogos(
							nextTeams.map((team) => team.image_url),
							TEAM_LOGO_CACHE_SIZE
						);
					}
				})
				.catch((error) => {
					if (!cancelled) {
						setLoadError(
							error.message || 'Could not load championship weeks.'
						);
					}
				})
				.finally(() => {
					if (!cancelled) setIsLoading(false);
				});

			return () => {
				cancelled = true;
			};
		},
		[
			isOpen
		]
	);

	const teamsById = useMemo(
		() =>
			teams.reduce((lookup, team) => {
				lookup[team.id] = team;
				return lookup;
			}, {}),
		[
			teams
		]
	);

	const playerTeam = playerTeamId ? teamsById[playerTeamId] || null : null;

	const loadTeamPlayers = useCallback((teamId) => {
		if (!teamId) return;

		setPlayerSlots((prev) => {
			const existing = prev[teamId];
			if (existing && (existing.loading || existing.players)) return prev;
			return { ...prev, [teamId]: { loading: true, error: '', players: null } };
		});

		fetch(teamPlayersUrl(teamId), {
			credentials: 'same-origin',
			headers: { Accept: 'application/json' }
		})
			.then(async (response) => {
				const payload = await response.json();
				if (!response.ok || !payload.success) {
					throw new Error(payload.error || 'Could not load team players.');
				}
				setPlayerSlots((prev) => ({
					...prev,
					[teamId]: {
						loading: false,
						error: '',
						players: (payload.data && payload.data.players) || []
					}
				}));
			})
			.catch((error) => {
				setPlayerSlots((prev) => ({
					...prev,
					[teamId]: {
						loading: false,
						error: error.message || 'Could not load team players.',
						players: null
					}
				}));
			});
	}, []);

	const handleToggleExpand = useCallback(
		(teamId) => {
			setExpandedTeamId((current) => {
				if (current === teamId) return null;

				const team = teamsById[teamId];
				if (team) {
					loadTeamPlayers(teamId);
					PLAYOFF_WEEKS.forEach((week) => {
						const weekMatchup =
							(team.weeks && (team.weeks[week] || team.weeks[String(week)])) ||
							null;
						if (weekMatchup && weekMatchup.opponent_id) {
							loadTeamPlayers(weekMatchup.opponent_id);
						}
					});
				}

				return teamId;
			});
		},
		[
			teamsById,
			loadTeamPlayers
		]
	);

	const handleWeekClick = useCallback(
		(week, matchup) => {
			if (!playerTeam) return;
			const opponent = teamsById[matchup.opponent_id];
			if (!opponent) return;
			setSelectedMatchup({ team: playerTeam, opponent, week, matchup });
		},
		[
			playerTeam,
			teamsById
		]
	);

	const handleCloseMatchupCompare = () => setSelectedMatchup(null);

	if (!isOpen) return null;

	return (
		<React.Fragment>
			<div
				className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 py-6"
				onClick={onClose}
				role="presentation"
			>
				<div
					className="relative w-full max-w-4xl rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-xl max-h-full overflow-y-auto"
					onClick={(event) => event.stopPropagation()}
					role="dialog"
					aria-modal="true"
					aria-labelledby="championship-weeks-modal-title"
				>
					<button
						type="button"
						onClick={onClose}
						className="absolute top-3 right-3 rounded-full p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-bg"
						aria-label="Close championship weeks"
					>
						<svg
							className="h-5 w-5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>

					<div className="p-6">
						<h2
							id="championship-weeks-modal-title"
							className="text-2xl font-bold text-gray-900 dark:text-dark-text pr-12"
						>
							{playerName}'s Championship Weeks
						</h2>

						<div className="mt-5">
							{isLoading && (
								<div className="flex items-center justify-center py-10 text-sm text-gray-500 dark:text-gray-400">
									Loading championship weeks…
								</div>
							)}

							{!isLoading &&
							loadError && (
								<div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900 dark:bg-opacity-20 p-4 text-sm text-red-700 dark:text-red-300">
									{loadError}
								</div>
							)}

							{!isLoading &&
							!loadError &&
							!playerTeam && (
								<div className="rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg p-4 text-sm text-gray-500 dark:text-gray-400">
									Championship weeks schedule is not available for this player's team.
								</div>
							)}

							{!isLoading &&
							!loadError &&
							playerTeam && (
								<TeamChampionshipWeeksCard
									team={playerTeam}
									teamsById={teamsById}
									isExpanded={expandedTeamId === playerTeam.id}
									playerSlots={playerSlots}
									onToggleExpand={handleToggleExpand}
									onWeekClick={handleWeekClick}
								/>
							)}
						</div>
					</div>
				</div>
			</div>

			<ChampionshipMatchupCompareModal
				isOpen={Boolean(selectedMatchup)}
				onClose={handleCloseMatchupCompare}
				team={selectedMatchup && selectedMatchup.team}
				opponent={selectedMatchup && selectedMatchup.opponent}
				week={selectedMatchup && selectedMatchup.week}
				matchup={selectedMatchup && selectedMatchup.matchup}
			/>
		</React.Fragment>
	);
};

export default ChampionshipWeeksModal;
